/**
 * Surface — THE card. Every rounded container on the platform is an
 * instance of this primitive.
 *
 * Brief 5 (2026-07-09) found 8 separate implementations of "rounded
 * surface with a border" (IhsCard, DefaultCard, MultiMarketStack.section,
 * PlayerPropsCard, MatchInsights, InsightsCard, VideoOddsStrip, HeroCard)
 * and 13 card headers — with only 2 of the 8 reading `--radius-card`.
 * Surface collapses all of them so a brand token change moves EVERY card.
 *
 * Design contract (all brand-overridable, defaults = Atlas-modern):
 *   --radius-card          outer radius            (default 14px)
 *   --radius-card-nested   radius of a Surface inside a Surface
 *                          (default: outer − 4px, floor 4px)
 *   --card-padding         body padding            (default 16px)
 *   --card-elevation       'border' | 'shadow' | 'flat' — resolved via
 *                          the `elevation` prop; border uses --border,
 *                          shadow uses --shadow-card
 *   --bg-surface           card fill (on-dark: --card-fill-on-dark)
 *   --border-subtle        header/body divider
 *
 * Slots:
 *   header  — leading (title + optional subtitle), trailing (meta /
 *             control), optional `bar` row beneath (toggles, legends).
 *   children — the body. `padding="none"` + the exported `bleed` class
 *             is the NAMED pattern for children that must reach the
 *             card edge (v4 flush odds group, MediaHero) — no more
 *             ad-hoc negative margins.
 *
 * Deliberately NOT props: colours, fonts, per-corner radii. If a case
 * can't be said with a token or a one-word enum, argue the case away
 * (Brief 5 constraint). Not an LLM-planner primitive — plans compose
 * LayoutGroups; Surface is the chrome those groups (and every widget)
 * render into.
 */

import type { ReactElement, ReactNode } from 'react';
import styles from './Surface.module.css';

export type SurfaceElevation = 'border' | 'shadow' | 'flat';
export type SurfacePadding = 'default' | 'none';

export interface SurfaceHeaderProps {
  title: ReactNode;
  /** Small, quiet, right of / beneath the title per the type ramp. */
  subtitle?: ReactNode;
  /** Right-aligned meta text, chevron or control. */
  trailing?: ReactNode;
  /** Optional full-width row under the title row (toggles, legends). */
  bar?: ReactNode;
}

export interface SurfaceProps {
  header?: SurfaceHeaderProps;
  /** 'border' (default) | 'shadow' | 'flat'. */
  elevation?: SurfaceElevation;
  /** 'none' for full-bleed children (odds groups, media heroes). */
  padding?: SurfacePadding;
  /** Set true when rendering on bg_primary / video (Stories overlays). */
  onDark?: boolean;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * Class for a child that must reach the card edge while siblings keep
 * the body padding. The documented bleed pattern — use this, never
 * hand-rolled negative margins.
 */
export const SURFACE_BLEED_CLASS = styles.bleed;

export function Surface({
  header,
  elevation = 'border',
  padding = 'default',
  onDark = false,
  children,
  className,
  'data-testid': testId,
}: SurfaceProps): ReactElement {
  const classes = [
    styles.surface,
    styles[`elevation-${elevation}`],
    padding === 'none' ? styles.padNone : styles.padDefault,
    onDark ? styles.onDark : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes} data-testid={testId}>
      {header ? (
        <header className={styles.header}>
          <div className={styles.headerRow}>
            <div className={styles.leading}>
              <h3 className={styles.title}>{header.title}</h3>
              {header.subtitle != null ? (
                <span className={styles.subtitle}>{header.subtitle}</span>
              ) : null}
            </div>
            {header.trailing != null ? (
              <div className={styles.trailing}>{header.trailing}</div>
            ) : null}
          </div>
          {header.bar != null ? <div className={styles.bar}>{header.bar}</div> : null}
        </header>
      ) : null}
      <div className={styles.body}>{children}</div>
    </section>
  );
}
